import React, { useMemo, useState } from 'react';

import { DateRange } from 'react-day-picker';
import { useParams } from 'react-router-dom';

import { Stack } from '@mui/material';
import useUsers from 'api/useUsers';
import { EFeatureFlag } from 'constants/enums';

import { AnalyticsBox } from 'components/AnalyticsBox/AnalyticsBox';
import { AnalyticsPieBox } from 'components/AnalyticsBox/AnalyticsPieBox/AnalyticsPieBox';
import DatePicker from 'components/DatePicker/DatePicker';
import { DatePickerMode } from 'components/DatePicker/DatePicker.types';

import { useAnalyticsData } from './hooks/use-analytics-data';
import { useXAxisDateFormat } from './hooks/useXAxisDateFormat';
import { useAnalyticsChartData } from './useAnalyticsChartData.hook';
import {
  DEFAULT_DATE_RANGE,
  FINANCE_BREAKDOWNS_ANALYTICS,
  getFixedDays,
  getWholeRange
} from './utils';

const AnalyticsFinanceTab: React.FC = () => {
  const { publisherId } = useParams();
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_DATE_RANGE);
  const [previousDateRange, setPreviousDateRange] = useState<
    DateRange | undefined
  >();

  const { fetchFeatureFlags } = useUsers({ publisherId });
  const shouldDisplaySciPlayAnalytics =
    fetchFeatureFlags.data?.featureFlags?.[
      EFeatureFlag.CHECKOUT_SCIPLAY_ANALYTICS
    ];

  const apiDateRange = useMemo(() => getWholeRange(dateRange), [dateRange]);
  const apiPreviousDateRange = useMemo(
    () => getWholeRange(previousDateRange),
    [previousDateRange]
  );

  const { analytics, previousAnalytics, isLoading, filteredAnalytics } =
    useAnalyticsData(
      apiDateRange,
      apiPreviousDateRange,
      publisherId,
      shouldDisplaySciPlayAnalytics
    );

  const handleDateRangeChange = (value: DateRange) => {
    setDateRange(value);
    setPreviousDateRange(undefined);
  };

  const formattedAnalytics = useAnalyticsChartData({
    stats: analytics.getAnalytics.data,
    trends: analytics.getTrends.data,
    previousStats: previousAnalytics.getAnalytics.data,
    previousTrends: previousAnalytics.getTrends.data,
    previousDateRange: previousDateRange,
    currentDateRange: dateRange
  });

  const xAxisDateFormat = useXAxisDateFormat(analytics);

  const breakdownAnalytics = FINANCE_BREAKDOWNS_ANALYTICS.map((metric) => {
    const breakdown = analytics.getBreakdowns.data?.find(
      (breakdown) => breakdown.name === metric
    );
    return breakdown;
  });

  return (
    <Stack direction="column">
      <div className="w-full mb-4 flex justify-end items-center pb-2">
        <div className="flex items-center w-[50%] gap-2">
          <DatePicker
            shouldDisplayTime={false}
            mode={DatePickerMode.RANGE}
            value={dateRange}
            onApply={(value) => handleDateRangeChange(value as DateRange)}
            shouldExcludeFuture
          />
          <DatePicker
            shouldDisplayTime={false}
            mode={DatePickerMode.RANGE}
            value={previousDateRange}
            onApply={(value) => setPreviousDateRange(value as DateRange)}
            onClear={() => setPreviousDateRange(undefined)}
            placeholder="Compare Dates"
            disabled={
              dateRange?.from === undefined || dateRange?.to === undefined
            }
            shouldExcludeFuture
            disableDatesAfter={dateRange.to}
            fixedRangeDays={getFixedDays(dateRange)}
          />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        {breakdownAnalytics.map((breakdown) => (
          <AnalyticsPieBox
            key={breakdown?.name}
            breakdown={breakdown}
            isLoading={isLoading}
            emptyState={'0'}
          />
        ))}
        {filteredAnalytics.financeAnalytics.map((metricName) => {
          const item = formattedAnalytics[metricName];
          return (
            <AnalyticsBox
              key={metricName}
              metricName={metricName}
              stat={item?.stat}
              currentTrends={item?.currentTrends ?? []}
              previousTrends={item?.previousTrends}
              isLoading={isLoading}
              xAxisDateFormat={xAxisDateFormat}
              emptyState={'0'}
            />
          );
        })}
      </div>
    </Stack>
  );
};

export default AnalyticsFinanceTab;
